import * as React from 'react';
import PrimaryMailFolderTreeContainer from './PrimaryMailFolderTreeContainer';
import {
    ArchiveMailFolderTreeContainer,
    SharedFolderTreeParentContainer,
} from './lazyFolderComponents';
import { observer } from 'mobx-react-lite';
import { LazyFolderPermissionsDialog } from 'owa-mail-folder-permissions';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { FolderHierarchyConfigurationQueryDocument } from '../graphql/__generated__/FolderHierarchyConfigurationQuery.interface';
import { useManagedQuery } from 'owa-apollo-hooks/lib/useManagedQuery';
import type { MailboxType } from 'owa-graph-schema';
import type { MailboxInfo } from 'owa-client-ids';

export interface MailFolderTreesParentContainerProps {
    isFavoriteOrGroupListShown: boolean;
    leftNavStyleWithSectionHeaders: string;
    leftNavStyleWithNoSectionHeaders: string;
    setSize: number;
    positionInSet: number;
}

/**
 * Parent container component hosting primary, archive and shared folder tree containers
 */
export default observer(function MailFolderTreesParentContainer(
    props: MailFolderTreesParentContainerProps
) {
    const {
        leftNavStyleWithSectionHeaders,
        leftNavStyleWithNoSectionHeaders,
        isFavoriteOrGroupListShown,
        setSize,
    } = props;

    let { positionInSet } = props;

    // Primary root node should be shown in case of any one of the Favorites or Groups or Archive mailbox or Shared Folders is shown
    const shouldShowPrimaryRootNode = isFavoriteOrGroupListShown || !isConsumer();
    const { loading, data } = useManagedQuery(FolderHierarchyConfigurationQueryDocument);

    if (loading) {
        return null;
    }

    if (!data) {
        return null;
    }

    const userConfig = data.userConfiguration;
    const userIdentity = userConfig.SessionSettings?.LogonEmailAddress;
    const archiveRootDisplayName = userConfig.SessionSettings?.ArchiveDisplayName;
    const archiveMailboxGuid = userConfig.SessionSettings?.ArchiveMailboxGuid;
    const organizationDomain = userConfig.SessionSettings?.OrganizationDomain;
    const isShadowMailbox = userConfig.SessionSettings?.IsShadowMailbox;
    const hasArchive = userConfig.SessionSettings?.HasArchive;
    const shouldShowArchiveTree =
        hasArchive &&
        userConfig.PolicySettings?.ShowOnlineArchiveEnabled &&
        archiveRootDisplayName != null;
    const mailboxType: MailboxType = 'UserMailbox';
    const mailboxSmtpAddress = userConfig.SessionSettings?.UserEmailAddress;
    const primaryMailboxInfo: MailboxInfo = {
        type: mailboxType,
        userIdentity: userIdentity,
        mailboxSmtpAddress: mailboxSmtpAddress,
    };

    return (
        <>
            <PrimaryMailFolderTreeContainer
                showRootNode={shouldShowPrimaryRootNode}
                className={
                    shouldShowPrimaryRootNode
                        ? leftNavStyleWithSectionHeaders
                        : leftNavStyleWithNoSectionHeaders
                }
                setSize={setSize}
                positionInSet={positionInSet}
                userConfiguration={userConfig}
                isShadowMailbox={isShadowMailbox}
                mailboxInfo={primaryMailboxInfo}
            />
            {!isConsumer() && (
                <>
                    {shouldShowArchiveTree && (
                        <ArchiveMailFolderTreeContainer
                            className={leftNavStyleWithSectionHeaders}
                            setSize={setSize}
                            positionInSet={++positionInSet}
                            archiveRootDisplayName={archiveRootDisplayName}
                            userIdentity={userIdentity}
                            archiveMailboxGuid={archiveMailboxGuid}
                            organizationDomain={organizationDomain}
                            isShadowMailbox={isShadowMailbox}
                            primaryMailboxInfo={primaryMailboxInfo}
                            userEmailAddress={userConfig.SessionSettings?.UserEmailAddress}
                        />
                    )}
                    <SharedFolderTreeParentContainer
                        className={leftNavStyleWithSectionHeaders}
                        setSize={setSize}
                        positionInSet={++positionInSet}
                        userIdentity={userIdentity}
                        isShadowMailbox={isShadowMailbox}
                        primaryMailboxInfo={primaryMailboxInfo}
                    />
                    <LazyFolderPermissionsDialog />
                </>
            )}
        </>
    );
});
